import { type ReactNode, type Ref, useEffect, useRef, useState } from "react";
import * as Video from "twilio-video";

// Type
type TwilioTracks = Array<Video.VideoTrack | Video.AudioTrack | Video.RemoteTrack | Video.LocalTrack | null>;

// Componentes
export default function Participant({
  participant,
  className = "",
  forceMuted = false,
  type,
  childs,
  ref,
  ...props
}: {
  participant: Video.Participant;
  className?: string;
  forceMuted?: boolean;
  type: string;
  childs?: ReactNode;
} & React.ComponentPropsWithRef<"div">) {
  const [videoTracks, setVideoTracks] = useState<TwilioTracks>([]);
  const [audioTracks, setAudioTracks] = useState<TwilioTracks>([]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const trackpubsToTracks = (trackMap: Map<string, Video.RemoteTrackPublication | Video.LocalTrackPublication>): TwilioTracks =>
    Array.from(trackMap.values())
      .map((publication) => publication.track)
      .filter((track) => track !== null);

  useEffect(() => {
    const trackSubscribed = (track: Video.AudioTrack | Video.VideoTrack) => {
      if (track.kind === "video") {
        setVideoTracks((videoTracks) => [...videoTracks, track]);
      } else {
        setAudioTracks((audioTracks) => [...audioTracks, track]);
      }
    };

    const trackUnsubscribed = (track: Video.Track) => {
      if (track.kind === "video") {
        setVideoTracks((videoTracks) => videoTracks.filter((v) => v !== track));
      } else {
        setAudioTracks((audioTracks) => audioTracks.filter((a) => a !== track));
      }
    };

    // =>
    setVideoTracks(trackpubsToTracks(participant.videoTracks));
    setAudioTracks(trackpubsToTracks(participant.audioTracks));

    // Get new publications
    participant.on("trackPublished", (trackPublication) => {
      if (trackPublication.track?.kind === "video") setVideoTracks([trackPublication.track]);

      if (trackPublication.track?.kind === "audio") setAudioTracks([trackPublication.track]);
    });

    participant.on("trackSubscribed", trackSubscribed);
    participant.on("trackUnsubscribed", trackUnsubscribed);

    return () => {
      setVideoTracks([]);
      setAudioTracks([]);
      // participant.removeAllListeners();
    };
  }, [participant]);

  useEffect(() => {
    const videoTrack = videoTracks[0] as Video.VideoTrack;

    if (videoTrack && videoRef.current) {
      videoTrack.attach(videoRef.current);

      return () => {
        videoTrack.detach();
      };
    }
  }, [videoTracks]);

  useEffect(() => {
    const audioTrack = audioTracks[0] as Video.AudioTrack;

    if (audioTrack && audioRef.current) {
      audioTrack.attach(audioRef.current);
      return () => {
        audioTrack.detach();
      };
    }
  }, [audioTracks]);

  let styles;

  const isPa = type === "local";

  const isSupervisor = participant.identity === "supervisor";

  // let ref:Ref<HTMLDivElement>;

  switch (type) {
    case "pa":
      styles = `w-[229px] rounded-md overflow-hidden aspect-[16/9] bg-black`;
      break;
    case "local":
      styles = `w-[229px] rounded-md overflow-hidden aspect-[16/9] bg-black cursor-grab`;
      break;

    case "uda":
      styles = `w-full max-h-full`;
      break;

    case "supervisor":
      styles = `w-[229px] rounded-md overflow-hidden border-feedback-negative-500 border-4`;
      break;
  }

  return (
    <div ref={ref} className={`participant relative ${styles} ${type} ${participant.identity} ${className}`} {...(isPa && { ...props })}>
      {childs && childs}
      <video ref={videoRef} className="w-full max-h-full" autoPlay={true} />
      <audio ref={audioRef} autoPlay={true} muted={forceMuted} />

      {isSupervisor && <span className="block w-fit absolute bottom-0 right-0 bg-feedback-negative-500 text-white p-2 rounded-ss-md">Supervisão</span>}
    </div>
  );
}
